/**
 * AuthContext —— 前端认证状态的单一来源。
 *
 * 持有当前登录用户、Site membership、当前 Site、权限码集合与加载态，
 * 暴露 login / logout / refreshSession / selectSite / hasPermission。
 * 身份令牌只存在于同源 HttpOnly Cookie，JavaScript 不读取或持久化 bearer。
 *
 * 生命周期：
 *   - 挂载时调 /api/auth/me 水合 user+permissions；401 时回到匿名态。
 *   - login(username,password) → 后端写 HttpOnly Cookie，再 me() 水合。
 *   - logout() → 后端清 Cookie，再置空状态。
 *
 * 前后端均以强制认证为安全默认；只有本地兼容场景可显式关闭。
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  authApi,
  type AuthenticatedUser,
  type EffectivePermissionGrant,
  type LinkedEmployee,
  type MeResult,
  type SiteMembership,
  type SiteReference,
} from '../services/authApi';
import {
  clearLegacyAuthToken,
  clearCurrentSitePublicId,
  getCurrentSitePublicId,
  readLegacyAuthTokenForMigration,
  setCurrentSitePublicId,
} from '../services/api';

export interface AuthContextValue {
  /** 当前登录用户；未登录 / 未水合时为 null。 */
  user: AuthenticatedUser | null;
  /** 权限码集合（permission_code），用于 hasPermission 快速查。 */
  permissions: Set<string>;
  /** 当前 Site 权限快照的可解释 Grant 来源。 */
  permissionGrants: EffectivePermissionGrant[];
  /** 平台控制面权限，与当前 Site 的角色授权严格分离。 */
  platformPermissions: Set<string>;
  /** 角色码列表（role_code）。 */
  roles: string[];
  /** 当前用户全部有效 Site membership。门禁阶段只应包含 MFG8。 */
  memberships: SiteMembership[];
  /** 当前权限快照对应的 Site；未登录 / 未水合时为 null。 */
  currentSite: SiteReference | null;
  /** 当前 membership 的授权版本；未登录时为 null。 */
  authzVersion: number | null;
  /** 当前登录用户绑定的员工档案；未绑定时为 null。 */
  linkedEmployee: LinkedEmployee | null;
  /** 是否正在进行启动水合 / 登录。 */
  loading: boolean;
  /** 用户是否需强制改密（登录返回的 mustChangePassword）。 */
  mustChangePassword: boolean;
  /** 登录：成功返回 mustChangePassword（true=须强制改密，调用方应停在登录页改密）；失败抛原始异常给调用方读 code/error。 */
  login: (username: string, password: string) => Promise<boolean>;
  /** 登出：清 token + 置空状态。 */
  logout: () => Promise<void>;
  /** 重新从 /auth/me 拉取当前 Site 的身份和权限快照。 */
  refreshSession: () => Promise<void>;
  /**
   * 选择 Site 并重新水合。服务端返回的 currentSite 必须与选择值一致，否则失败关闭。
   * 当前 readiness gate 阶段只会使用 MFG8。
   */
  selectSite: (sitePublicId: string) => Promise<void>;
  /** 权限判定：权限集合内含该码即为 true。 */
  hasPermission: (code: string) => boolean;
  /** 平台控制面权限判定。 */
  hasPlatformPermission: (code: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [permissionGrants, setPermissionGrants] = useState<EffectivePermissionGrant[]>([]);
  const [platformPermissions, setPlatformPermissions] = useState<Set<string>>(new Set());
  const [memberships, setMemberships] = useState<SiteMembership[]>([]);
  const [currentSite, setCurrentSite] = useState<SiteReference | null>(null);
  const [authzVersion, setAuthzVersion] = useState<number | null>(null);
  const [linkedEmployee, setLinkedEmployee] = useState<LinkedEmployee | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [mustChangePassword, setMustChangePassword] = useState<boolean>(false);

  const clearSession = useCallback(() => {
    clearCurrentSitePublicId();
    setUser(null);
    setPermissions(new Set());
    setPermissionGrants([]);
    setPlatformPermissions(new Set());
    setMemberships([]);
    setCurrentSite(null);
    setAuthzVersion(null);
    setLinkedEmployee(null);
    setMustChangePassword(false);
  }, []);

  const applySession = useCallback((session: MeResult) => {
    setCurrentSitePublicId(session.currentSite?.publicId ?? null);
    setUser(session.user);
    setPermissions(new Set(session.permissions));
    setPermissionGrants(session.permissionGrants ?? []);
    setPlatformPermissions(new Set(session.platformPermissions ?? []));
    setMemberships(session.memberships);
    setCurrentSite(session.currentSite);
    setAuthzVersion(session.authzVersion);
    setLinkedEmployee(session.linkedEmployee);
  }, []);

  const refreshSession = useCallback(async () => {
    const session = await authApi.me();
    applySession(session);
  }, [applySession]);

  // 启动水合：Cookie 对 JavaScript 不可见，因此始终向 /me 查询一次。
  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      const legacyToken = readLegacyAuthTokenForMigration();
      if (legacyToken) {
        try {
          await authApi.upgradeLegacySession(legacyToken);
        } catch {
          // Expired/invalid legacy credentials fall through to the normal Cookie /me check.
        } finally {
          clearLegacyAuthToken();
        }
      }
      try {
        const session = await authApi.me();
        if (cancelled) return;
        applySession(session);
      } catch {
        // Cookie 失效 / 网络失败：回到匿名态（拦截器也会处理 401 跳转）。
        if (!cancelled) {
          clearLegacyAuthToken();
          clearSession();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [applySession, clearSession]);

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    setLoading(true);
    clearLegacyAuthToken();
    clearSession();
    try {
      const result = await authApi.login(username, password);
      try {
        const session = await authApi.me();
        applySession(session);
        setMustChangePassword(result.mustChangePassword);
      } catch (error) {
        clearLegacyAuthToken();
        clearSession();
        throw error;
      }
      return result.mustChangePassword;
    } finally {
      setLoading(false);
    }
  }, [applySession, clearSession]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* 后端登出失败不阻塞前端清理 */
    } finally {
      clearLegacyAuthToken();
      clearSession();
    }
  }, [clearSession]);

  const selectSite = useCallback(async (sitePublicId: string) => {
    const normalized = sitePublicId.trim();
    const membership = memberships.find(
      (item) => item.membershipStatus === 'ACTIVE' && item.site.publicId === normalized,
    );
    if (!membership) {
      throw new Error('Site membership is not active');
    }

    const previousSitePublicId = getCurrentSitePublicId();
    setCurrentSitePublicId(normalized);
    try {
      const session = await authApi.me(normalized);
      if (session.currentSite?.publicId !== normalized) {
        throw new Error('Site session does not match the selected Site');
      }
      applySession(session);
    } catch (error) {
      setCurrentSitePublicId(previousSitePublicId);
      throw error;
    }
  }, [applySession, memberships]);

  const hasPermission = useCallback(
    (code: string) => permissions.has(code),
    [permissions],
  );
  const hasPlatformPermission = useCallback(
    (code: string) => platformPermissions.has(code),
    [platformPermissions],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      permissions,
      permissionGrants,
      platformPermissions,
      roles: user?.roles ?? [],
      memberships,
      currentSite,
      authzVersion,
      linkedEmployee,
      loading,
      mustChangePassword,
      login,
      logout,
      refreshSession,
      selectSite,
      hasPermission,
      hasPlatformPermission,
    }),
    [
      user,
      permissions,
      permissionGrants,
      platformPermissions,
      memberships,
      currentSite,
      authzVersion,
      linkedEmployee,
      loading,
      mustChangePassword,
      login,
      logout,
      refreshSession,
      selectSite,
      hasPermission,
      hasPlatformPermission,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth 必须在 <AuthProvider> 内部使用');
  }
  return ctx;
};
